import { FC, ReactElement } from 'react';
import { NumberFormatValues, NumericFormat, NumericFormatProps } from 'react-number-format';
// mui components
import { Grid, IconButton, InputAdornment } from '@mui/material';
// components
import OutlinedInput, { IOutlinedInputProps } from './outlined-input';
// types
import { TInputArrowClickFunction } from 'utils/types';
// icons
import ArrowUpIcon from 'assets/icons/buttons/chevron-up.svg';
import ArrowDownIcon from 'assets/icons/buttons/chevron-down.svg';

export interface IProps extends Omit<NumericFormatProps<IOutlinedInputProps>, 'onChange'> {
  onChange?: (value?: string | number) => void;
  showStartAdornment?: boolean;
  showEndAdornment?: boolean;
  adornmentContent?: ReactElement;
  handleArrowClick?: TInputArrowClickFunction;
  disableUpArrow?: boolean;
  disableDownArrow?: boolean;
}

const arrowButtonStyles = {
  padding: '0',
  '&.Mui-disabled': {
    pointerEvents: 'visible',
    cursor: 'default',
  },
};

const NumericFormatInput: FC<IProps> = ({
  onChange,
  showStartAdornment,
  showEndAdornment,
  adornmentContent,
  handleArrowClick,
  disableDownArrow = false,
  disableUpArrow = false,
  ...rest
}) => {
  const arrows = (
    <Grid sx={{ display: 'flex', flexDirection: 'column' }}>
      <IconButton
        sx={arrowButtonStyles}
        onClick={() => {
          handleArrowClick?.('up');
        }}
        disabled={disableUpArrow}
      >
        <ArrowUpIcon />
      </IconButton>
      <IconButton
        sx={arrowButtonStyles}
        onClick={() => {
          handleArrowClick?.('down');
        }}
        disabled={disableDownArrow}
      >
        <ArrowDownIcon />
      </IconButton>
    </Grid>
  );

  const onValueChange = (values: NumberFormatValues) => {
    onChange(values.floatValue);
  };

  const renderAdornment = () => (
    <InputAdornment position={showStartAdornment ? 'start' : 'end'}>
      <Grid
        item
        sx={{
          '& .MuiInputAdornment-root': {
            marginRight: '0',
          },
        }}
      >
        {adornmentContent ?? arrows}
      </Grid>
    </InputAdornment>
  );

  return (
    <NumericFormat
      {...rest}
      startAdornment={showStartAdornment ? renderAdornment() : undefined}
      endAdornment={showEndAdornment ? renderAdornment() : undefined}
      customInput={OutlinedInput}
      onValueChange={onValueChange}
    />
  );
};

export default NumericFormatInput;
